import queryClient from 'core/api/query.config';
import { logClient } from 'core/log';
import {
  clearWebViewCookies,
  deleteCachedCameraOriginals,
} from 'core/platform/device-data.service';
import type { AuthContextProps } from 'oidc-react';
import { selectKeysToRemove } from './sign-out.business';
import { KEPT_LOCAL_STORAGE_KEYS } from './sign-out.constants';

const clearStorage = (storage: Storage, keptKeys?: string[]): void => {
  selectKeysToRemove(Object.keys(storage), keptKeys).forEach(key => storage.removeItem(key));
};

const runSafely = async (step: string, action: () => unknown): Promise<void> => {
  try {
    await action();
  } catch (error) {
    logClient.warn(`Sign-out could not ${step}`, error);
  }
};

const clearDevice = async (auth: AuthContextProps): Promise<void> => {
  await runSafely('remove the local user', () => auth.signOut());
  queryClient.clear();
  clearStorage(globalThis.localStorage, KEPT_LOCAL_STORAGE_KEYS);
  clearStorage(globalThis.sessionStorage);
  await runSafely('clear the web view cookies', clearWebViewCookies);
  await runSafely('delete cached camera originals', deleteCachedCameraOriginals);
};

const canReachIdentityProvider = (): boolean => globalThis.navigator.onLine;

const reloadClearedApp = (): void => globalThis.location.replace('/');

let pendingSignOut: Promise<void> | undefined;

const signOut = async (auth: AuthContextProps): Promise<void> => {
  const idTokenHint = auth.userData?.id_token;

  await clearDevice(auth);

  if (!canReachIdentityProvider()) {
    reloadClearedApp();
    return;
  }
  await runSafely('redirect to the identity provider', () =>
    auth.signOutRedirect({ id_token_hint: idTokenHint })
  );
};

export const signOutAndClearDevice = (auth: AuthContextProps): Promise<void> => {
  pendingSignOut ??= signOut(auth).finally(() => {
    pendingSignOut = undefined;
  });
  return pendingSignOut;
};
